import {
  RestoreNoTransactionError,
  calculateMonthlyPrice,
  classifyPurchaseError,
  failureOf,
  createSubscribeFlow,
  removeTrailingZeros,
  withFailure,
  readOwnerUid,
} from '@forest/paywall-core'
import { TrialTimelineView, useTrialTimelineDialog } from '@forest/shared/react'
import { useStore } from '@nanostores/react'
import { useMutation } from '@tanstack/react-query'
import isNetworkError from 'is-network-error'
import { isHTTPError, isTimeoutError } from 'ky'
import { useState, useTransition } from 'react'
import { useChinaAndroidTrialMutation } from '~/hooks/use-china-android-trial-mutation'
import { AbortError, getAuthenticatedUserInfo } from '~/lib/get-user-info'
import { openIntercom } from '~/lib/intercom'
import { posthog } from '~/lib/posthog'
import { m } from '~/paraglide/messages'
import { platform } from '~/params'
import { api } from '~/services/api'
import { native } from '@forest/native-services'
import { $chinaAndroidTrialEligibility, $product } from '~/store'
import { $debugAfterPaymentError } from '~/store/debug'
import { $receiptSystemAccountId } from '~/store/receipt-system-account-id'
import { dialog } from '~/store/dialog'
import type { ProductMetadata } from '~/types/iap'
import { LoadingOverlay } from './loading-overlay'
import { SubscribedView } from './subscribed-view'
import { showSubscriptionConflictDialog } from '~/lib/subscription-conflict-dialog'
import { showPurchaseConfirmationDialog } from '~/lib/purchase-confirmation-dialog'

const isChinaAndroid = platform === 'china_android'

const flow = createSubscribeFlow<ProductMetadata>({
  api,
  platform,
  native: {
    purchase: native.iap.purchase,
    closeTransaction: native.iap.closeTransaction,
    getAllUnclosedTransactions: native.iap.getAllUnclosedTransactions,
    getAllEntitlements: native.iap.getAllEntitlements,
    logEvent: native.general.logEvent,
    refreshServiceLevel: () => native.user.refreshServiceLevel(),
  },
  telemetry: {
    captureException: (error, context) =>
      posthog?.captureException(error, context),
  },
  getUserId: async () => {
    const userInfo = await getAuthenticatedUserInfo()
    return userInfo.user_id
  },
  getReceiptSystemAccountId: () => $receiptSystemAccountId.get(),
  getDebugSimulate: () => $debugAfterPaymentError.get(),
})

export function FixedBottomCTA() {
  const product = useStore($product)
  const chinaAndroidTrialEligibility = useStore($chinaAndroidTrialEligibility)
  const [isActionPending, startActionTransition] = useTransition()
  const [isSubscribedViewOpen, setIsSubscribedViewOpen] = useState(false)
  const timeline = useTrialTimelineDialog()

  const isChinaAndroidTrialEligible =
    chinaAndroidTrialEligibility === 'available'

  const eligibleTrialDays = import.meta.env.DEV
    ? 7
    : product?.eligible_trial_days
  const isTrialEligible = (eligibleTrialDays ?? 0) > 0

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async () => {
      if (import.meta.env.DEV) {
        return {
          skuId: 'dev',
          serviceLevelStatus: {
            service_level: 'subscription',
            is_premium: false,
            subscription: {
              expires_at: new Date(
                Date.now() + 1000 * 60 * 60 * 24 * 30,
              ).toISOString(),
            },
          },
        }
      }

      if (!product) throw new Error('never')

      const serviceLevelStatus = await flow.run(product)
      return {
        skuId: product.sku,
        serviceLevelStatus,
      }
    },
    onSuccess: ({ skuId, serviceLevelStatus }) => {
      native.general.logEvent({
        event_name: 'product_sku_purchased_success',
        event_params: {
          sku_id: skuId,
        },
      })
      const successEventName =
        isChinaAndroid || !isTrialEligible
          ? 'plus_subscribe_success'
          : 'plus_trial_success'
      native.general.logEvent({
        event_name: successEventName,
        event_params: {
          sku_id: skuId,
        },
      })

      let task = Promise.resolve()
      if (
        !isChinaAndroid &&
        isTrialEligible &&
        serviceLevelStatus?.service_level === 'subscription'
      ) {
        task = (async function showGrantPermissionDialogIfNeeded() {
          if (import.meta.env.DEV) {
            await timeline.openPermissionDialog(
              serviceLevelStatus.subscription.expires_at,
            )
            return
          }
          let result
          try {
            result = await native.permission.getHasNotificationPermission()
          } catch {
            return
          }
          if (result.success && !result.data) {
            await timeline.openPermissionDialog(
              serviceLevelStatus.subscription.expires_at,
            )
          }
        })()
      }
      task.then(() => {
        setIsSubscribedViewOpen(true)
        native.user.refreshServiceLevel()
      })
    },
    onError: (error) => {
      if (error instanceof AbortError) return

      const intent = classifyPurchaseError(error)
      switch (intent.kind) {
        case 'payment_timeout':
          dialog.open({
            title: m.dialog_payment_timeout_title(),
            description: withFailure(
              m.dialog_payment_timeout_content({ error: intent.detail }),
              intent.failure,
            ),
            button: m.OK(),
          })
          return
        case 'illegal_transaction_restore':
          dialog.open({
            title: m.dialog_illegal_transaction_title(),
            description: withFailure(
              platform === 'china_android'
                ? m.dialog_illegal_transaction_content_cna()
                : m.dialog_illegal_transaction_content(),
              intent.failure,
            ),
            button: m.restore_purchase(),
            closeButton: true,
            asyncAction: async () => {
              dialog.setLoading(true)
              try {
                await flow.restore()
                dialog.close()
                dialog.open({
                  title: m.dialog_restore_success_title(),
                  description: m.dialog_restore_success_content(),
                  button: m.awesome(),
                  action: () => {
                    native.user.refreshServiceLevel()
                    native.general.close()
                  },
                })
              } catch (restoreError) {
                dialog.close()
                await showRestoreErrorDialog(restoreError)
              }
            },
          })
          return
        case 'no_internet':
          dialog.open({
            title: m.dialog_no_internet_title(),
            description: withFailure(
              m.dialog_no_internet_content(),
              intent.failure,
            ),
            button: m.OK(),
          })
          return
        case 'http_error':
          dialog.open({
            title: m.dialog_unknown_error_title(),
            description: withFailure(
              m.dialog_unknown_error_content({
                error_code: intent.failure.code,
              }),
              intent.failure,
              { codeInDescription: true },
            ),
            button: m.contact_cs(),
            action: () => {
              openIntercom({
                node: 'subscribe_create_purchase_unexpected_http_error',
                orderId: intent.failure.purchaseId?.toString(),
                additional: { error_code: intent.failure.code },
              })
            },
          })
          return
        case 'confirmation_pending':
          showPurchaseConfirmationDialog(intent.failure)
          return
        case 'subscription_conflict':
          void showSubscriptionConflictDialog(intent.ownerUid, intent.failure)
          return
        case 'unknown':
          posthog?.captureException(error, {
            failure_code: intent.failure.code,
          })
          dialog.open({
            title: m.dialog_unknown_error_title(),
            description: withFailure(
              m.dialog_unknown_error_content({
                error_code: intent.failure.code,
              }),
              intent.failure,
              { codeInDescription: true },
            ),
            button: m.contact_cs(),
            action: () => {
              openIntercom({
                node: 'subscribe_create_purchase_unexpected_error',
                orderId: intent.failure.purchaseId?.toString(),
                additional: { error_code: intent.failure.code },
              })
            },
          })
          return
        default:
          intent satisfies never
      }
    },
  })

  const {
    mutateAsync: startChinaAndroidTrial,
    isPending: isChinaAndroidTrialPending,
  } = useChinaAndroidTrialMutation({
    onSuccess: () => {
      setIsSubscribedViewOpen(true)
      native.general.logEvent({
        event_name: 'product_sku_purchased_success',
        event_params: {
          sku_id: 'cna_plus_trial_7',
        },
      })
      native.general.logEvent({
        event_name: 'plus_trial_success',
        event_params: {
          sku_id: 'cna_plus_trial_7',
        },
      })
    },
  })

  const handleButtonClick = () => {
    if (!isChinaAndroid && isTrialEligible) {
      timeline.openTimeline()
      native.general.logEvent({
        event_name: 'page_view',
        event_params: {
          page_name: 'allowlist_paywall_trial_timeline',
        },
      })
      return
    }

    if (isActionPending || isPending || isChinaAndroidTrialPending) return
    startActionTransition(async () => {
      const isTrial = isChinaAndroid && isChinaAndroidTrialEligible
      native.general.logEvent({
        event_name: 'component_event',
        event_params: {
          type: 'btn',
          app_action: 'click',
          component_name: isTrial
            ? `allowlist_paywall_trial`
            : `allowlist_paywall_subscribe`,
        },
      })
      try {
        if (isChinaAndroid && isChinaAndroidTrialEligible) {
          await startChinaAndroidTrial()
        } else {
          await mutateAsync()
        }
      } catch {}
    })
  }

  // for timeline view
  const startTrial = async () => {
    native.general.logEvent({
      event_name: 'component_event',
      event_params: {
        component_name: 'allowlist_paywall_trial_timeline',
        app_action: 'click',
        type: 'btn',
      },
    })
    if (isPending) return
    await mutateAsync()
  }

  const getButtonText = () => {
    if (!product) return ''

    if (isChinaAndroid) {
      if (isChinaAndroidTrialEligible) {
        return m.onboarding_paywall_start_triasl_btn_cna({
          trial_day_number: 7,
        })
      }
      const price = removeTrailingZeros(product.price_localized)
      return m.allowlist_paywall_subscription_btn({
        discount_yearly_price: price,
      })
    }

    if (isTrialEligible) {
      return m.daily_paywall_tree_cta_btn()
    }

    const price = removeTrailingZeros(product.price_localized)
    return m.new_user_yearly_subscription_btn({ discount_yearly_price: price })
  }

  const getFooterText = () => {
    if (!product) return ''

    if (isChinaAndroid) {
      if (isChinaAndroidTrialEligible) {
        return m.onboarding_paywall_footer_trial_cna()
      }
      return m.onboarding_paywall_footer_cna()
    }

    if (isTrialEligible) {
      return m.daily_paywall_tree_notice()
    }

    const monthlyPrice = calculateMonthlyPrice({
      price_localized: product.price_localized,
      price_value: product.price_value,
    })
    return m.new_user_yearly_subscription_footer({
      yearly_price_month: monthlyPrice,
    })
  }

  const getTrialTimelinePrice = () => {
    if (!product) return ''
    return removeTrailingZeros(product.price_localized)
  }

  return (
    <>
      <div
        className="fixed bottom-0 left-0 right-0 h-[200px]"
        style={{
          backgroundImage:
            'linear-gradient(to bottom, #0000 0%, #147A6430 58%, #0D585630 100%)',
        }}
      />
      <div className="fixed bottom-0 left-0 right-0 h-[200px]">
        <ProgressiveBlur />
      </div>
      <div
        className="fixed bottom-0 left-0 right-0 flex h-[200px] translate-y-full flex-col items-center justify-end gap-4 px-6 pb-12 transition-transform duration-700 data-[loaded=true]:translate-y-0"
        data-loaded={!!product}
      >
        <p
          className="w-full text-center text-xs text-white"
          style={{
            textShadow: '0 0 2.8px #176361',
          }}
        >
          {getFooterText()}
        </p>
        <button
          className="group h-[52px] w-full max-w-[640px] rounded-xl p-px [background-image:linear-gradient(180deg,#C5FF70_0%,#69FF78_45%,#30D3FF_100%)]"
          onClick={handleButtonClick}
          disabled={isPending || isChinaAndroidTrialPending || isActionPending}
        >
          <span className="flex h-full w-full items-center justify-center rounded-[11px] font-bold text-[#176361] [background-image:linear-gradient(106deg,#C5FF70_13%,#30D3FF_97%)] group-active:[background-image:linear-gradient(90deg,#94D66E_0%,#2BB8D3_100%)]">
            {getButtonText()}
          </span>
        </button>
      </div>

      <TrialTimelineView
        {...timeline.timelineProps}
        m={m}
        backgroundLayer={
          <div
            className="size-full"
            style={{
              backgroundImage:
                'radial-gradient(435.59% 132.81% at 50.13% 151.23%, #2FC9C5 0%, #1C7977 82.21%, #176361 100%)',
            }}
          />
        }
        eligibleTrialDays={eligibleTrialDays ?? 0}
        planPrice={getTrialTimelinePrice()}
        startTrial={startTrial}
        requestNotificationPermission={async () => {
          try {
            await native.permission.requestNotificationPermission()
          } catch {
            // ignore
          }
        }}
      />

      <LoadingOverlay
        isOpen={isPending || isChinaAndroidTrialPending || isActionPending}
      />

      <SubscribedView isOpen={isSubscribedViewOpen} />
    </>
  )
}

function ProgressiveBlur() {
  const maskGradients = [
    [
      'rgba(0, 0, 0, 0) 0%',
      'rgba(0, 0, 0, 1) 12.5%',
      'rgba(0, 0, 0, 1) 25%',
      'rgba(0, 0, 0, 0) 37.5%',
    ],
    [
      'rgba(0, 0, 0, 0) 12.5%',
      'rgba(0, 0, 0, 1) 25%',
      'rgba(0, 0, 0, 1) 37.5%',
      'rgba(0, 0, 0, 0) 50%',
    ],
    [
      'rgba(0, 0, 0, 0) 25%',
      'rgba(0, 0, 0, 1) 37.5%',
      'rgba(0, 0, 0, 1) 50%',
      'rgba(0, 0, 0, 0) 62.5%',
    ],
    [
      'rgba(0, 0, 0, 0) 37.5%',
      'rgba(0, 0, 0, 1) 50%',
      'rgba(0, 0, 0, 1) 62.5%',
      'rgba(0, 0, 0, 0) 75%',
    ],
    [
      'rgba(0, 0, 0, 0) 50%',
      'rgba(0, 0, 0, 1) 62.5%',
      'rgba(0, 0, 0, 1) 75%',
      'rgba(0, 0, 0, 0) 87.5%',
    ],
    [
      'rgba(0, 0, 0, 0) 62.5%',
      'rgba(0, 0, 0, 1) 75%',
      'rgba(0, 0, 0, 1) 87.5%',
      'rgba(0, 0, 0, 0) 100%',
    ],
    ['rgba(0, 0, 0, 0) 75%', 'rgba(0, 0, 0, 1) 87.5%', 'rgba(0, 0, 0, 1) 100%'],
    ['rgba(0, 0, 0, 0) 87.5%', 'rgba(0, 0, 0, 1) 100%'],
  ]
  const maxBlur = 18
  const blurStep = maxBlur / (maskGradients.length - 1)

  return maskGradients.map((mask, index) => {
    const blur = Math.round(blurStep * index * 1000) / 1000
    const maskImage = `linear-gradient(to bottom, ${mask.join(', ')})`
    return (
      <div
        key={`${mask.join('-')}-${blur}`}
        className="absolute inset-0"
        style={{
          zIndex: index + 1,
          backdropFilter: `blur(${blur}px)`,
          WebkitBackdropFilter: `blur(${blur}px)`,
          maskImage,
          WebkitMaskImage: maskImage,
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
          maskSize: '100% 100%',
          WebkitMaskSize: '100% 100%',
          willChange: 'backdrop-filter',
        }}
      />
    )
  })
}

async function showRestoreErrorDialog(error: unknown) {
  const failure = failureOf(error, 'restore')
  if (error instanceof RestoreNoTransactionError) {
    dialog.open({
      title: m.dialog_restore_error_title(),
      description: withFailure(
        platform === 'china_android'
          ? m.dialog_restore_no_transaction_content_cna()
          : m.dialog_restore_no_transaction_content({
              app_store_account:
                platform === 'ios' ? m.apple_id() : m.google_play_store_id(),
            }),
        failure,
      ),
      button: m.OK(),
    })
    return
  }

  if (isHTTPError(error)) {
    const status = error.response.status
    if ((status === 409 || status === 412) && platform !== 'china_android') {
      await showSubscriptionConflictDialog(await readOwnerUid(error), failure)
      return
    }
    if (status === 403 && platform !== 'china_android') {
      dialog.open({
        title: m.dialog_restore_error_title(),
        description: withFailure(
          m.dialog_restore_error_forest_account_content({
            app_store_account:
              platform === 'ios' ? m.apple_id() : m.google_play_store_id(),
          }),
          failure,
        ),
        button: m.OK(),
      })
      return
    }
    if (
      status === 410 ||
      ((status === 403 || status === 409) && platform === 'china_android')
    ) {
      dialog.open({
        title: m.dialog_transaction_expired_title(),
        description: withFailure(
          m.dialog_transaction_expired_content(),
          failure,
        ),
        button: m.OK(),
      })
      return
    }
    dialog.open({
      title: m.dialog_restore_error_title(),
      description: withFailure(
        m.dialog_unknown_error_content({ error_code: failure.code }),
        failure,
        { codeInDescription: true },
      ),
      button: m.contact_cs(),
      action: () => {
        openIntercom({
          node: 'restore_purchase_unexpected_http_error',
          orderId: failure.purchaseId?.toString(),
          additional: { error_code: failure.code },
        })
      },
    })
    return
  }

  if (isNetworkError(error) || isTimeoutError(error)) {
    dialog.open({
      title: m.dialog_no_internet_title(),
      description: withFailure(m.dialog_no_internet_content(), failure),
      button: m.OK(),
    })
    return
  }

  dialog.open({
    title: m.dialog_restore_error_title(),
    description: withFailure(
      m.dialog_unknown_error_content({ error_code: failure.code }),
      failure,
      { codeInDescription: true },
    ),
    button: m.contact_cs(),
    action: () => {
      openIntercom({
        node: 'restore_purchase_unexpected_error',
        orderId: failure.purchaseId?.toString(),
        additional: { error_code: failure.code },
      })
    },
  })
}
